import { useCallback, useEffect, useMemo, useState } from "react";

const buildApiUrl = (apiBase, path, extraParams = {}) => {
  const params = new URLSearchParams();

  Object.entries(extraParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, value);
    }
  });

  const qs = params.toString();
  return qs ? `${apiBase}${path}?${qs}` : `${apiBase}${path}`;
};

const AUTH_CACHE_KEY = "linecontrol.auth";
const AUTH_CACHE_TTL_MS = 10 * 60 * 1000;

const readAuthCache = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
      window.localStorage.removeItem(AUTH_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn("Failed to read auth cache:", error);
    return null;
  }
};

const writeAuthCache = (isAuthenticated) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  if (!isAuthenticated) {
    window.localStorage.removeItem(AUTH_CACHE_KEY);
    return;
  }
  const payload = {
    isAuthenticated: true,
    expiresAt: Date.now() + AUTH_CACHE_TTL_MS,
  };
  window.localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(payload));
};

const useAuthSession = (apiBase) => {
  const cachedAuth = readAuthCache();
  const [isAuthenticated, setIsAuthenticated] = useState(
    cachedAuth?.isAuthenticated === true
  );
  const [authChecked, setAuthChecked] = useState(
    cachedAuth?.isAuthenticated === true
  );
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const resolvedApiBase = useMemo(() => apiBase, [apiBase]);

  const fetchWithAuth = useCallback(
    async (path, options = {}) => {
      const { method = "GET", body, params, cache } = options;
      const response = await fetch(buildApiUrl(resolvedApiBase, path, params), {
        method,
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
        credentials: "include",
        ...(cache ? { cache } : {}),
      });

      if (response.status === 403) {
        setIsAuthenticated(false);
        writeAuthCache(false);
      }
      return response;
    },
    [resolvedApiBase]
  );

  const checkSession = useCallback(async () => {
    try {
      const response = await fetchWithAuth("/session");
      if (response.ok) {
        setIsAuthenticated(true);
        writeAuthCache(true);
      } else {
        setIsAuthenticated(false);
        writeAuthCache(false);
      }
    } catch (error) {
      console.error("Session check error:", error);
      setIsAuthenticated(false);
    } finally {
      setAuthChecked(true);
    }
  }, [fetchWithAuth]);

  const loginSubmitHandler = useCallback(
    async (event) => {
      event.preventDefault();
      setLoginLoading(true);
      setLoginError("");
      try {
        const response = await fetchWithAuth("/login", {
          method: "POST",
          body: { username: loginUsername, password: loginPassword },
        });
        if (!response.ok) {
          setLoginError("Invalid username or password");
          return;
        }
        setIsAuthenticated(true);
        writeAuthCache(true);
      } catch (error) {
        console.error("Login error:", error);
        setLoginError("Login failed. Try again.");
      } finally {
        setLoginLoading(false);
      }
    },
    [fetchWithAuth, loginUsername, loginPassword]
  );

  const logoutHandler = useCallback(async () => {
    try {
      await fetchWithAuth("/logout", { method: "POST" });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsAuthenticated(false);
      writeAuthCache(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    if (!authChecked) {
      checkSession();
    }
  }, [authChecked, checkSession]);

  return {
    authChecked,
    isAuthenticated,
    loginUsername,
    setLoginUsername,
    loginPassword,
    setLoginPassword,
    loginError,
    loginLoading,
    loginSubmitHandler,
    logoutHandler,
    fetchWithAuth,
    buildApiUrl: (path, params) => buildApiUrl(resolvedApiBase, path, params),
  };
};

export default useAuthSession;
